import { FC, useRef } from "react";
import {
  Box,
  Chip,
  IconButton,
  Slide,
  Typography,
  useMediaQuery,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import PictureInPictureAltIcon from "@mui/icons-material/PictureInPictureAlt";
import CastIcon from "@mui/icons-material/Cast";
import CastConnectedIcon from "@mui/icons-material/CastConnected";
import LabelIcon from "@mui/icons-material/Label";
import LabelOffIcon from "@mui/icons-material/LabelOff";
import { useLabelsVisibility, useTranslation } from "@/shared/hooks";
import { SeekBar } from "./SeekBar";
import type { PlayerTimecode } from "./playerTimecode";
import { VolumeControl } from "./VolumeControl";
import { QualityMenu, SpeedMenu } from "./SettingsMenus";
import { formatPlayerTime } from "./formatPlayerTime";
import { isAtLiveEdge } from "./liveEdge";
import { playerFooterControlPadding } from "./playerFooterControlPadding";
import type { useVideoEngine } from "./useVideoEngine";

type Engine = ReturnType<typeof useVideoEngine>;

interface PlayerFooterProps {
  src?: string | null;
  state: Engine["state"];
  actions: Engine["actions"];
  visible: boolean;
  fullscreen: boolean;
  onToggleFullscreen: () => void;
  onTogglePip: () => void;
  timecodes?: PlayerTimecode[] | null;
  /** True for a currently-live stream (StreamScreen passes
   * stream.status === "STARTED") — swaps the time display for a clickable
   * LIVE/rewound badge instead. */
  isLive?: boolean;
  /** Показывать ли кнопку Chromecast (SDK загрузился и в сети есть хотя бы
   * один приёмник) — см. Player/cast/useCast.ts. */
  castAvailable?: boolean;
  /** Активная cast-сессия транслирует именно этот контент. */
  casting?: boolean;
  onCast?: () => void;
}

export const PlayerFooter: FC<PlayerFooterProps> = ({
  src,
  state,
  actions,
  visible,
  fullscreen,
  onToggleFullscreen,
  onTogglePip,
  timecodes,
  isLive,
  castAvailable,
  casting,
  onCast,
}) => {
  const isNarrow = useMediaQuery("(max-width:450px)");
  const [labelsVisible, toggleLabelsVisibility] = useLabelsVisibility();
  const { t } = useTranslation();
  // Тумблер показывается при наличии любых меток — как в референсном
  // PlayerFooter (LabelsToggleButton при timecodes.length > 0).
  const hasTimecodes = Boolean(timecodes && timecodes.length > 0);
  // At the edge the seek bar is pinned to the right and the badge stays
  // plain LIVE: the HLS window slides every second, so raw
  // currentTime/duration would jitter. The offset is shown only once the
  // viewer rewinds into the DVR window. Tolerance and hysteresis: liveEdge.ts.
  const atLiveEdgeRef = useRef(false);
  const atLiveEdge = isAtLiveEdge({
    wasAtEdge: atLiveEdgeRef.current,
    liveEdge: state.liveEdge,
    currentTime: state.currentTime,
    targetDuration: state.liveTargetDuration,
  });
  atLiveEdgeRef.current = atLiveEdge;

  return (
    <Slide direction="up" in={visible} appear={false}>
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          pb: 0.5,
          pointerEvents: visible ? "auto" : "none",
          "&:before": {
            content: '""',
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: 64,
            pointerEvents: "none",
            backgroundImage:
              "linear-gradient(transparent, rgba(0,0,0,0.6), rgba(0,0,0,0.9))",
          },
        }}
      >
        {/* Сикбар почти во всю ширину плеера (минимальные отступы), у ряда
            кнопок ниже — свои, побольше. */}
        <Box sx={{ px: { xs: 0.5, sm: 1 } }}>
          {/* Remounting on src change clears any in-progress drag state so a
              stale drag position is never committed against the new video. */}
          <SeekBar
            key={src ?? ""}
            currentTime={
              isLive && atLiveEdge ? state.duration : state.currentTime
            }
            duration={state.duration}
            buffered={state.buffered}
            onSeek={actions.seek}
            timecodes={timecodes}
          />
        </Box>
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            minHeight: 56,
            px: { xs: 1, sm: 2, md: 3 },
            "@media (max-width: 450px)": { px: 0.5 },
          }}
        >
          <IconButton
            size="large"
            onClick={actions.togglePlay}
            sx={(theme) => ({
              color: "#fff",
              ...playerFooterControlPadding(theme),
            })}
          >
            {state.paused ? <PlayArrowIcon /> : <PauseIcon />}
          </IconButton>
          {isLive && state.liveEdge != null ? (
            <Chip
              label={
                atLiveEdge
                  ? t("stream.live.badge")
                  : `${t("stream.live.badge")} -${formatPlayerTime(
                      state.liveEdge - state.currentTime,
                    )}`
              }
              size="small"
              onClick={actions.goLive}
              sx={{
                ml: 0.5,
                cursor: "pointer",
                "&, &:hover": {
                  bgcolor: atLiveEdge ? "error.main" : "grey.700",
                },
                color: "#fff",
                fontWeight: 700,
              }}
            />
          ) : (
            <Typography
              variant="caption"
              sx={{
                color: "#fff",
                ml: 0.5,
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
              }}
            >
              {formatPlayerTime(state.currentTime)} /{" "}
              {formatPlayerTime(state.duration)}
            </Typography>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <VolumeControl
            volume={state.volume}
            muted={state.muted}
            onToggleMute={actions.toggleMute}
            onSetVolume={actions.setVolume}
          />
          <QualityMenu
            levels={state.levels}
            currentLevel={state.currentLevel}
            autoLevel={state.autoLevel}
            onSetLevel={actions.setLevel}
          />
          {hasTimecodes && (
            <IconButton
              size="large"
              onClick={toggleLabelsVisibility}
              sx={(theme) => ({
                color: "#fff",
                opacity: labelsVisible ? 1 : 0.6,
                ...playerFooterControlPadding(theme),
              })}
              aria-label={
                labelsVisible
                  ? t("player.controls.hideLabels")
                  : t("player.controls.showLabels")
              }
            >
              {labelsVisible ? <LabelIcon /> : <LabelOffIcon />}
            </IconButton>
          )}
          {!isNarrow && !casting && (
            <SpeedMenu
              playbackRate={state.playbackRate}
              onSetRate={actions.setPlaybackRate}
            />
          )}
          {!isNarrow && (
            <IconButton
              size="large"
              onClick={onTogglePip}
              sx={(theme) => ({
                color: "#fff",
                ...playerFooterControlPadding(theme),
              })}
              aria-label={t("player.controls.pictureInPicture")}
            >
              <PictureInPictureAltIcon />
            </IconButton>
          )}
          {castAvailable && (
            <IconButton
              size="large"
              onClick={onCast}
              sx={(theme) => ({
                color: "#fff",
                ...playerFooterControlPadding(theme),
              })}
              aria-label={
                casting
                  ? t("player.controls.castConnected")
                  : t("player.controls.cast")
              }
            >
              {casting ? <CastConnectedIcon /> : <CastIcon />}
            </IconButton>
          )}
          <IconButton
            size="large"
            onClick={onToggleFullscreen}
            sx={(theme) => ({
              color: "#fff",
              ...playerFooterControlPadding(theme),
            })}
          >
            {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Box>
      </Box>
    </Slide>
  );
};
